import { api } from '../slices/api';

/**
 * A session either finished quickly enough to be believable, dragged on past
 * the threshold the caller chose, or is young enough to still be underway.
 * Only `completed` sessions feed the averages.
 */
export type KittingSessionStatus = 'completed' | 'timeout' | 'in_progress';

export interface KittingSessionRow {
	id: string;
	order_line_item_id: string;
	/** "Order / part" label. */
	line_item: string;
	started_at: string;
	completed_at: string | null;
	/** Wall-clock gap, reported for timeouts too; null while still open. */
	elapsed_seconds: number | null;
	status: KittingSessionStatus;
	/** True for every kit of a job after the first. */
	is_reprint: boolean;
	started_by_user_id: number | null;
	/**
	 * The account whose session sent the print, NOT reliably the person who
	 * did the kitting: the tablet screen never asks who is standing there.
	 * Empty when no signed-in account was attached. Read it as provenance.
	 */
	started_by: string;
	completed_by_user_id: number | null;
	completed_by: string;
	tag_count: number;
}

export interface KittingLogResponse {
	results: KittingSessionRow[];
}

export interface KittingTotals {
	sessions: number;
	completed: number;
	timeouts: number;
	in_progress: number;
	completed_first_attempt: number;
	completed_reprint: number;
	timeouts_first_attempt: number;
	timeouts_reprint: number;
	job_count: number;
	account_count: number;
	/** Null rather than 0 when nothing completed: no average exists. */
	average_duration_seconds: number | null;
	average_duration_seconds_first_attempt: number | null;
	average_duration_seconds_reprint: number | null;
}

export interface KittingDailyRow {
	date: string;
	sessions: number;
	completed: number;
	timeouts: number;
	average_duration_seconds: number | null;
}

export interface KittingAccount {
	id: number;
	name: string;
}

export interface KittingSummaryResponse {
	start: string;
	end: string;
	timeout_minutes: number;
	timezone: string;
	totals: KittingTotals;
	/**
	 * Every account that kitted in the window, regardless of the account
	 * filter -- so the filter this populates cannot erase its own options.
	 */
	accounts: KittingAccount[];
	daily: KittingDailyRow[];
}

export interface KittingReportParams {
	/** ISO 8601; the window is half-open, `[start, end)`. */
	start: string;
	end: string;
	/** Elapsed time past which a session is bad data. Backend default is 45. */
	timeoutMinutes?: number;
	userIds?: number[];
	orderLineItemIds?: string[];
	/** IANA zone the daily buckets are cut on (summary only). */
	tz?: string;
}

const buildQuery = ({
	start,
	end,
	timeoutMinutes,
	userIds,
	orderLineItemIds,
	tz,
}: KittingReportParams) => {
	const params = new URLSearchParams({ start, end });
	if (timeoutMinutes) params.set('timeout_minutes', String(timeoutMinutes));
	if (userIds?.length) params.set('user_ids', userIds.join(','));
	if (orderLineItemIds?.length)
		params.set('order_line_item_ids', orderLineItemIds.join(','));
	if (tz) params.set('tz', tz);
	return params.toString();
};

export const kittingReportApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getKittingLog: builder.query<KittingLogResponse, KittingReportParams>({
			query: (params) => `orders/reports/kitting-log/?${buildQuery(params)}`,
		}),
		getKittingSummary: builder.query<
			KittingSummaryResponse,
			KittingReportParams
		>({
			query: (params) => `orders/reports/kitting-summary/?${buildQuery(params)}`,
		}),
	}),
});

export const { useGetKittingLogQuery, useGetKittingSummaryQuery } =
	kittingReportApi;
